import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { api } from "../lib/api";
import { ArrowLeft, RefreshCw, Loader2, MessageCircle, Calendar, Mail, ChevronDown } from "lucide-react";

const fmt = (s) => { try { return new Date(s).toLocaleString("de-CH", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return s; } };

export default function SatBotAdmin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "admin") { navigate("/admin/login"); return; }
    load();
    // eslint-disable-next-line
  }, [user, loading]);

  const load = async () => {
    setItems(null);
    const r = await api.get("/admin/satbot/bookings");
    setItems(r.data);
  };

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/satbot/bookings/${id}/status?status=${status}`);
    await load();
  };

  if (loading || !user) return <div className="pt-32 text-center text-white/55 bg-[#081328] min-h-screen">Lade…</div>;

  return (
    <div className="min-h-screen bg-[#081328] text-[#F7F6F2] pt-24" data-testid="satbot-admin">
      <div className="container-sat max-w-6xl py-10">
        <div className="flex items-center justify-between mb-8">
          <Link to="/admin" className="inline-flex items-center gap-2 text-white/55 hover:text-[#D4AF37] text-sm">
            <ArrowLeft size={16} /> Zurück
          </Link>
          <button onClick={load} className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#D4AF37]">
            <RefreshCw size={14} /> Neu laden
          </button>
        </div>

        <div className="mb-10">
          <div className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-3">SATbot · Eingänge</div>
          <h1 className="serif text-4xl md:text-5xl font-light tracking-tight">Termine &amp; Anfragen.</h1>
          <p className="text-white/55 mt-3">Jede Konversation wird hier ausgewertet. Erfolgreiche Intent-Detection erzeugt einen Eintrag.</p>
        </div>

        {items === null && <div className="text-center py-16"><Loader2 className="animate-spin text-[#D4AF37] mx-auto" /></div>}
        {items?.length === 0 && <div className="text-center py-16 text-white/45">Noch keine Anfragen.</div>}

        <div className="space-y-3">
          {items?.map((it) => (
            <div key={it.id} className="border border-white/10 rounded-lg overflow-hidden bg-white/[0.02]" data-testid={`booking-${it.id}`}>
              <button onClick={() => setExpanded(expanded === it.id ? null : it.id)} className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/[0.03]">
                <div className="flex items-center gap-3">
                  {it.type === "appointment" ? (
                    <Calendar size={16} className="text-[#48d597]" />
                  ) : (
                    <Mail size={16} className="text-[#a48cff]" />
                  )}
                  <div className="text-left">
                    <div className="font-medium">{it.payload?.first_name || "—"} <span className="text-white/45 text-xs ml-2">{it.payload?.email}</span></div>
                    <div className="text-xs text-white/45 mt-0.5">
                      {it.type === "appointment" ? `Termin: ${it.payload?.slot_iso ? fmt(it.payload.slot_iso) : "—"}` : `Anfrage: ${it.payload?.interest || "—"}`}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${
                    it.status === "new" ? "border-[#D4AF37]/50 text-[#D4AF37]" :
                    it.status === "contacted" ? "border-[#a48cff]/50 text-[#a48cff]" :
                    it.status === "scheduled" ? "border-[#48d597]/50 text-[#48d597]" :
                    "border-white/20 text-white/45"
                  }`}>{it.status}</span>
                  <span className="text-xs text-white/40">{fmt(it.created_at)}</span>
                  <ChevronDown size={14} className={`text-white/40 transition ${expanded === it.id ? "rotate-180" : ""}`} />
                </div>
              </button>
              {expanded === it.id && (
                <div className="px-5 py-4 border-t border-white/5 bg-black/20">
                  <div className="text-xs text-white/45 mb-2">Details</div>
                  <pre className="text-xs bg-black/30 p-3 rounded overflow-x-auto text-white/75">{JSON.stringify(it.payload, null, 2)}</pre>
                  <div className="flex flex-wrap gap-2 mt-4">
                    {["new", "contacted", "scheduled", "closed", "rejected"].map((s) => (
                      <button key={s} onClick={() => updateStatus(it.id, s)}
                        className={`px-3 py-1.5 text-xs rounded border transition ${
                          it.status === s ? "bg-[#D4AF37] text-[#081328] border-[#D4AF37]" : "border-white/15 text-white/65 hover:border-[#D4AF37]/50 hover:text-[#D4AF37]"
                        }`}
                        data-testid={`status-${s}-${it.id}`}>{s}</button>
                    ))}
                  </div>
                  {it.session_id && (
                    <div className="text-xs text-white/40 mt-3">Session: <code>{it.session_id}</code></div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
